import React, { useEffect, useState, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

export default function AreaYearlyExpenseChart({ years, currency, selectedGlobalYear }) {
  const history = useHistory();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedYear, setSelectedYear] = useState(
    selectedGlobalYear || new Date().getFullYear()
  );
  const [monthlyExpenseData, setMonthlyExpenseData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatToK = (num) => {
    const value = Number(num || 0);

    if (Math.abs(value) >= 1000000) return (value / 1000000).toFixed(1) + "M";
    if (Math.abs(value) >= 1000) return (value / 1000).toFixed(0) + "k";
    return value.toString();
  };

  // Sync global year
  useEffect(() => {
    if (selectedGlobalYear) setSelectedYear(selectedGlobalYear);
  }, [selectedGlobalYear]);

  // Auth check
  useEffect(() => {
    if (!localStorage.getItem("userData")) history.push("/login");
  }, [history]);

  const storedUserData = localStorage.getItem("userData");
  const groupId = storedUserData ? JSON.parse(storedUserData).groupId : "";

  // Dark mode detection
  useEffect(() => {
    const checkDark = () =>
      document.documentElement.classList.contains("dark");

    setIsDarkMode(checkDark());

    const observer = new MutationObserver(() =>
      setIsDarkMode(checkDark())
    );

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const formatNumber = useMemo(
    () => (num) => Number(num || 0).toLocaleString("en-US"),
    []
  );

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!groupId || !selectedYear) {
        setMonthlyExpenseData([]);
        setCategoryData([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const data = await apiService.getYearlyExpense(groupId, selectedYear);

        const allMonths = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
        ];

        const currentYear = new Date().getFullYear();
        const currentMonthIndex = new Date().getMonth();

        const visibleMonths =
          selectedYear === currentYear
            ? allMonths.slice(0, currentMonthIndex + 1)
            : allMonths;

        // Collect unique categories
        const allCategoryNames = new Set();
        data.forEach((item) => {
          Object.keys(item.categories || {}).forEach((cat) =>
            allCategoryNames.add(cat)
          );
        });
        const uniqueCategories = Array.from(allCategoryNames);

        // Monthly main data
        const transformed = visibleMonths.map((month, idx) => {
          const found = data.find(
            (item) => parseInt(item.month, 10) - 1 === idx
          );

          return {
            month,
            totalDebit: found?.totalDebit || 0,
            totalCredit: found?.totalCredit || 0,
            balance: found?.balance || 0,
            categories: found?.categories || {},
          };
        });

        // Category breakdown
        const categoryMap = visibleMonths.map((month, idx) => {
          const found = data.find(
            (item) => parseInt(item.month, 10) - 1 === idx
          );
          const cats = found?.categories || {};

          return uniqueCategories.reduce(
            (acc, cat) => ({ ...acc, [cat]: cats[cat] || 0 }),
            { month }
          );
        });

        setMonthlyExpenseData(transformed);
        setCategoryData(categoryMap);
      } catch (err) {
        console.error(err);
        setMonthlyExpenseData([]);
        setCategoryData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, groupId]);

  const tooltipFormatter = useMemo(() => {
    return (params) => {
      if (!params?.length) return "";

      const index = params[0].dataIndex;
      const monthData = monthlyExpenseData[index];
      if (!monthData) return "";

      const seriesInfo = params
        .map(
          (p) =>
            `<div>
              <span style="color:${p.color};font-weight:600;">●</span>
              ${p.seriesName}: ${currency}${formatToK(p.value)}
            </div>`
        )
        .join("");

      const categoryInfo = categoryData[index]
        ? Object.entries(categoryData[index])
          .filter(([key]) => key !== "month")
          .map(
            ([cat, val]) =>
              `<div style="color:${isDarkMode ? "#d1d5db" : "#666"};">
                 ${cat}: ${currency}${formatToK(val)}
                </div>`
          )
          .join("")
        : "";

      return `
        <strong>${monthData.month}</strong><br/>
        ${seriesInfo}
        <hr/>
        ${categoryInfo}
      `;
    };
  }, [monthlyExpenseData, categoryData, isDarkMode, currency, formatNumber]);

  const xAxisData = useMemo(
    () => monthlyExpenseData.map((d) => d.month),
    [monthlyExpenseData]
  );
  const expensesData = useMemo(
    () => monthlyExpenseData.map((d) => d.totalDebit),
    [monthlyExpenseData]
  );
  const creditsData = useMemo(
    () => monthlyExpenseData.map((d) => d.totalCredit),
    [monthlyExpenseData]
  );
  const netBalanceData = useMemo(
    () => monthlyExpenseData.map((d) => d.balance),
    [monthlyExpenseData]
  );

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        backgroundColor: isDarkMode ? "#374151" : "#fff",
        borderColor: isDarkMode ? "#4b5563" : "#e5e7eb",
        textStyle: { color: isDarkMode ? "#f9fafb" : "#111827" },
        formatter: tooltipFormatter,
      },
      legend: {
        data: ["Expenses", "Credits", "Net Balance"],
        textStyle: { color: isDarkMode ? "#e5e7eb" : "#374151" },
      },
      grid: {
        left: "40px",
        right: "40px",
        top: "50px",
        bottom: "40px",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxisData,
        axisLabel: {
          color: isDarkMode ? "#d1d5db" : "#374151",
        },
      },
      yAxis: [
        {
          type: "value",
          name: `Amount (${currency})`,
          axisLabel: {
            formatter: (v) => `${currency} ${formatToK(v)}`,
            color: isDarkMode ? "#d1d5db" : "#374151",
          },
        },
      ],
      series: [
        {
          name: "Expenses",
          type: "bar",
          data: expensesData,
          itemStyle: { color: "#2563eb" },
        },
        {
          name: "Credits",
          type: "bar",
          data: creditsData,
          itemStyle: { color: "#22c55e" },
        },
        {
          name: "Net Balance",
          type: "line",
          yAxisIndex: 0,
          data: netBalanceData,
          itemStyle: { color: "#f59e0b" },
          smooth: true,
        },
      ],
    }),
    [
      tooltipFormatter,
      xAxisData,
      expensesData,
      creditsData,
      netBalanceData,
      isDarkMode,
      currency,
      formatNumber,
    ]
  );
  return (
    <div className=" bg-white dark:bg-gray-800 shadow-lg rounded-lg w-full mt-0 pb-2">
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "20px 20px 0px 20px" }}>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2 lg:mb-0">Yearly Credit, Debit & Net Balance</h2>
        <select
          style={{
            padding: "6px 15px",
            borderRadius: "6px",
            color: isDarkMode ? "#fff" : "#333",
            border: "solid #ddd 1px",
            minWidth: "80px"
          }}
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {/* Chart */}
      {loading ? (
        <div style={{ height: "420px", display: "flex", justifyContent: "center", alignItems: "center", color: isDarkMode ? "#ccc" : "#666" }}>
          Loading chart...
        </div>
      ) : monthlyExpenseData.length === 0 ? (
        <div style={{ height: "420px", display: "flex", justifyContent: "center", alignItems: "center", color: isDarkMode ? "#ccc" : "#666" }}>
          No data available for selected year.
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <div
            style={{
              minWidth: window.innerWidth < 768 ? "700px" : "100%",
            }}
          >
            <ReactECharts option={option} style={{ height: "420px" }} />
          </div>
        </div>
      )}
    </div>
  );
}